import React, { useEffect, useState } from "react";
import axios from "../axios";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import {
    format,
    startOfMonth,
    endOfDay,
    endOfMonth,
    setMonth,
    eachDayOfInterval,
    isWithinInterval,
} from "date-fns";
import { sl } from "date-fns/locale";
import DatePicker from "react-datepicker";

export function ProjectsPage() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);

    const [selectedTimeFrame, setSelectedTimeFrame] = useState("po_mesecih");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const timeFilters = [
        // Months
        {
            label: "Celotno leto",
            value: "po_mesecih",
            getDate: () => {
                const now = new Date();
                const year = now.getFullYear();
                const start = startOfMonth(new Date(year, 0)); // January 1st
                const end = endOfMonth(new Date(year, 11)); // December 31st
                return [start, end];
            },
        },
        {
            label: "Januar",
            value: "jan",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 0));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Februar",
            value: "feb",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 1));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Marec",
            value: "mar",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 2));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "April",
            value: "apr",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 3));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Maj",
            value: "maj",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 4));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Junij",
            value: "jun",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 5));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Julij",
            value: "jul",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 6));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Avgust",
            value: "avg",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 7));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "September",
            value: "sep",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 8));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Oktober",
            value: "okt",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 9));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "November",
            value: "nov",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 10));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "December",
            value: "dec",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 11));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        { label: "Po meri", value: "custom", getDate: () => dateRange },
    ];

    const getSelectedTimeFrameLabel = () => {
        const filter = timeFilters.find((t) => t.value === selectedTimeFrame);
        if (filter.value === "custom" && startDate && endDate) {
            return `${format(startDate, "dd.MM.yyyy")} - ${format(
                endDate,
                "dd.MM.yyyy"
            )}`;
        }
        return filter ? filter.label.toUpperCase() : "NEZNANO OBDOBJE";
    };

    const handleTimeFrameSelect = (filter) => {
        setSelectedTimeFrame(filter.value);
        if (filter.value === "custom") {
            setShowDatePicker(true);
        } else {
            const [start, end] = filter.getDate();
            setDateRange([start, end]);
            setShowDatePicker(false);
        }
    };

    useEffect(() => {
        async function fetchProjectsData() {
            setLoading(true);
            try {
                const response = await axios.get(`/api/racuni/projects/info`, {
                    withCredentials: true,
                });

                setProjects(response.data);

                const groupedProjects = Object.values(
                    response.data.reduce((acc, { name, price, date }) => {
                        acc[name] = acc[name] || {
                            name,
                            price: 0,
                            date: date || "",
                        };
                        acc[name].price += parseFloat(price.replace(",", "."));
                        acc[name].date = date || "";
                        return acc;
                    }, {})
                );
                setFilteredProjects(groupedProjects);
                setError(null);
            } catch (error) {
                console.error("Error fetching agents data:", error);
                setError(
                    "Napaka pri nalaganju podatkov. Prosim, poskusite znova kasneje."
                );
            } finally {
                setLoading(false);
            }
        }

        fetchProjectsData();
    }, []);

    useEffect(() => {
        // For "po_mesecih", show all projects
        if (selectedTimeFrame === "po_mesecih") {
            const groupedProjects = Object.values(
                projects.reduce((acc, { name, price, date }) => {
                    acc[name] = acc[name] || {
                        name,
                        price: 0,
                        date: date || "",
                    };
                    acc[name].price += parseFloat(price.replace(",", "."));
                    acc[name].date = date || "";
                    return acc;
                }, {})
            );
            setFilteredProjects(groupedProjects);
            return;
        }

        // For "custom", use the dateRange
        if (selectedTimeFrame === "custom") {
            if (!startDate || !endDate) {
                const groupedProjects = Object.values(
                    projects.reduce((acc, { name, price, date }) => {
                        acc[name] = acc[name] || {
                            name,
                            price: 0,
                            date: date || "",
                        };
                        acc[name].price += parseFloat(price.replace(",", "."));
                        acc[name].date = date || "";
                        return acc;
                    }, {})
                );
                setFilteredProjects(groupedProjects);
                return;
            }

            const filtered = projects.filter((project) => {
                if (!project.date) return false;

                // Parse project date from "MM/DD/YYYY" format
                const [month, day, year] = project.date.split("/");
                const projectDate = new Date(year, month - 1, day);

                return isWithinInterval(projectDate, {
                    start: startDate,
                    end: endOfDay(endDate),
                });
            });

            // Group projects by name and sum prices
            const groupedProjects = Object.values(
                filtered.reduce((acc, { name, price, date }) => {
                    acc[name] = acc[name] || {
                        name,
                        price: 0,
                        date: date || "",
                    };
                    acc[name].price += parseFloat(price.replace(",", "."));
                    acc[name].date = date || "";
                    return acc;
                }, {})
            );
            setFilteredProjects(groupedProjects);
            return;
        }

        // For specific months (jan, feb, mar, etc.)
        const filter = timeFilters.find((f) => f.value === selectedTimeFrame);
        if (filter) {
            const [monthStart, monthEnd] = filter.getDate();

            const filtered = projects.filter((project) => {
                if (!project.date) return false;

                // Parse project date from "MM/DD/YYYY" format
                const [month, day, year] = project.date.split("/");
                const projectDate = new Date(year, month - 1, day);

                return isWithinInterval(projectDate, {
                    start: monthStart,
                    end: endOfDay(monthEnd),
                });
            });

            // Group projects by name and sum prices
            const groupedProjects = Object.values(
                filtered.reduce((acc, { name, price, date }) => {
                    acc[name] = acc[name] || {
                        name,
                        price: 0,
                        date: date || "",
                    };
                    acc[name].price += parseFloat(price.replace(",", "."));
                    acc[name].date = date || "";
                    return acc;
                }, {})
            );

            setFilteredProjects(groupedProjects);
        }
    }, [selectedTimeFrame, dateRange, projects, startDate, endDate]);

    return (
        <>
            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-b-2 border-gray-900 rounded-full animate-spin"></div>
                </div>
            )}

            {error && (
                <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded">
                    {error}
                </div>
            )}
            {!loading && !error && (
                <>
                    <div className="p-4 mb-6 bg-white rounded-lg shadow-md">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            <div className="space-y-2">
                                <label className="block font-semibold text-gray-700">
                                    Izberi časovno obdobje:
                                </label>
                                <Menu as="div" className="relative">
                                    <MenuButton className="flex items-center justify-between w-full px-3 py-2 text-sm text-left bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                        <span>
                                            {getSelectedTimeFrameLabel()}
                                        </span>
                                        <svg
                                            className="w-5 h-5 text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </MenuButton>
                                    <MenuItems className="absolute z-10 w-full mt-1 overflow-y-auto scrolling-touch bg-white border rounded-lg shadow-lg focus:outline-none max-h-60 sm:max-h-none">
                                        <div className="py-1">
                                            {timeFilters.map((filter) => (
                                                <MenuItem key={filter.value}>
                                                    {({ active }) => (
                                                        <button
                                                            className={`${
                                                                active
                                                                    ? "bg-blue-50 text-blue-700"
                                                                    : "text-gray-700"
                                                            } ${
                                                                selectedTimeFrame ===
                                                                filter.value
                                                                    ? "bg-blue-50 font-medium"
                                                                    : ""
                                                            } w-full px-4 py-2 text-left text-sm hover:bg-blue-50 transition-colors duration-150`}
                                                            onClick={() =>
                                                                handleTimeFrameSelect(
                                                                    filter
                                                                )
                                                            }
                                                        >
                                                            {filter.label}
                                                        </button>
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </div>
                                    </MenuItems>
                                </Menu>
                                {showDatePicker && (
                                    <div className="absolute z-20 p-4 mt-1 -translate-x-1/2 bg-white border rounded-lg shadow-lg left-1/2">
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(update) => {
                                                setDateRange(update);
                                                if (update[0] && update[1]) {
                                                    setShowDatePicker(false);
                                                }
                                            }}
                                            isClearable={true}
                                            inline
                                            className="react-datepicker"
                                            calendarClassName="custom-calendar"
                                            dayClassName={(date) =>
                                                date >= startDate &&
                                                date <= endDate
                                                    ? "bg-blue-50 text-blue-700"
                                                    : undefined
                                            }
                                        />
                                        <div className="flex justify-end mt-4 space-x-2">
                                            <button
                                                onClick={() =>
                                                    setShowDatePicker(false)
                                                }
                                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                            >
                                                Prekliči
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (startDate && endDate) {
                                                        setShowDatePicker(
                                                            false
                                                        );
                                                    }
                                                }}
                                                className="px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                                                disabled={
                                                    !startDate || !endDate
                                                }
                                            >
                                                Potrdi
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="space-y-8">
                        <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                            <div className="px-6 py-4 border-b border-blue-100 bg-blue-50">
                                <h2 className="text-xl font-bold text-blue-900">
                                    PROJEKTI
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="mb-6 overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th
                                                    scope="col"
                                                    className="sticky left-0 px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase bg-gray-50"
                                                >
                                                    Ime projekta
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                                >
                                                    Cena projekta
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredProjects.map(
                                                (project, index) => (
                                                    <tr
                                                        key={index}
                                                        className={
                                                            index % 2 === 0
                                                                ? "bg-white"
                                                                : "bg-gray-50"
                                                        }
                                                    >
                                                        <td className="sticky left-0 px-3 py-2 text-sm font-medium whitespace-nowrap bg-inherit">
                                                            {project.name}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                            {project.price
                                                                .toFixed(2)
                                                                .toString()
                                                                .replace(
                                                                    ".",
                                                                    ","
                                                                )}{" "}
                                                            €
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
